// src/shell/auth.js — екран за вход, стартиране (boot), таймер за бездействие
// и keepalive. Пренесено дословно от app.js (Фаза 3.3).

import { el, clear, $, field, input } from '../shared/dom.js';
import { api } from '../shared/api.js';
import { state } from '../shared/state.js';
import { openModal, closeModal, modalDraft } from '../shared/modal.js';
import { renderShell } from './shell.js';
import { MIN as PASS_MIN, passwordError } from '../../services/passwordPolicy.js';

export async function renderAuth() {
  const root = $('#app');
  clear(root);
  let mode = 'login';
  let setupNeeded = false;
  try { const s = await api('/auth/status'); setupNeeded = s.setup_needed; if (setupNeeded) mode = 'register'; } catch (e) {}
  // Линкът от писмото „Забравена парола" (#/reset/<код>) отваря екрана за
  // нова парола (Промени 16.10.2026).
  let resetToken = '';
  try { const m = /^#\/reset\/([A-Za-z0-9]+)$/.exec(window.location.hash || ''); if (m) { resetToken = m[1]; mode = 'reset'; } } catch (e) {}

  const card = el('div', { class: 'auth-card' });
  function draw() {
    clear(card);
    if (mode === 'forgot' || mode === 'reset') return drawRecovery();
    const errBox = el('div');
    const name = input({ type: 'text', placeholder: 'Вашето име' });
    const email = input({ type: 'email', placeholder: 'name@example.com' });
    const pass = input({ type: 'password', placeholder: '••••••••' });
    // Кодът за настройка (ПРОМТ-6а): само при първата регистрация. Отива в
    // заглавието X-Setup-Token, не в тялото — сървърът го сверява със
    // SETUP_TOKEN от средата. Тип password: кодът е таен като парола.
    // autocomplete: браузърът да не бърка кода с „потвърди паролата" и да не
    // предлага да го запази — той е еднократен.
    const setupCode = input({ type: 'password', placeholder: 'от настройките на сървъра', autocomplete: 'one-time-code' });
    pass.autocomplete = mode === 'register' ? 'new-password' : 'current-password';

    // (А.6) Око: показва/скрива паролата — най-честата причина за „грешна
    // парола" е невидимо сгрешен знак.
    const eye = el('button', { class: 'pass-eye', type: 'button', 'aria-label': 'Покажи паролата', 'aria-pressed': 'false', text: '👁' });
    eye.addEventListener('click', () => {
      const show = pass.type === 'password';
      pass.type = show ? 'text' : 'password';
      eye.setAttribute('aria-pressed', String(show));
      eye.setAttribute('aria-label', show ? 'Скрий паролата' : 'Покажи паролата');
      pass.focus();
    });
    const passWrap = el('div', { class: 'pass-wrap' }, pass, eye);

    // (А.6) Caps Lock — глухата причина №2. role="status": казва се учтиво.
    const capsNote = el('div', { class: 'caps-note', role: 'status', text: '⇪ Внимание: включен е Caps Lock — буквите излизат главни.' });
    capsNote.hidden = true;
    const syncCaps = (e) => { if (e.getModifierState) capsNote.hidden = !e.getModifierState('CapsLock'); };
    pass.addEventListener('keydown', syncCaps);
    pass.addEventListener('keyup', syncCaps);
    pass.addEventListener('blur', () => { capsNote.hidden = true; });

    const submit = async () => {
      errBox.innerHTML = '';
      try {
        if (mode === 'register') {
          await api('/auth/register', {
            method: 'POST',
            // trim: копиран код често носи интервал накрая, а fetch и без това
            // би го отрязал мълчаливо — по-добре явно.
            headers: { 'Content-Type': 'application/json', 'X-Setup-Token': setupCode.value.trim() },
            body: JSON.stringify({ name: name.value, email: email.value, password: pass.value }),
          });
        } else {
          await api('/auth/login', { method: 'POST', body: JSON.stringify({ email: email.value, password: pass.value }) });
        }
        // Успешен вход: обяснението за авто-изхода вече не е нужно.
        try { sessionStorage.removeItem('crm.idleLogout'); } catch (e) {}
        await boot();
      } catch (e) {
        // Сървърните текстове са български; тук се превежда само крайният
        // случай 401 БЕЗ тяло (прокси), който api.js дава като общото
        // „Възникна грешка." — при вход това значи грешни данни.
        let msg = e.message;
        if (mode === 'login' && msg === 'Възникна грешка.') msg = 'Грешен имейл или парола.';
        clear(errBox); errBox.appendChild(el('div', { class: 'err', role: 'alert', text: msg }));
      }
    };

    card.appendChild(el('div', { class: 'auth-logo' }, el('img', { class: 'mark', src: '/logo.png', alt: 'BV KONT' }), el('span', { text: 'CRM система' })));
    card.appendChild(el('p', { class: 'auth-sub', text: setupNeeded ? 'Първоначална настройка — създайте администраторски профил.' : 'Влезте в профила си, за да продължите.' }));
    // (А.5а) След авто-изход обяснението стои ПОСТОЯННО на екрана за вход —
    // изчезващият toast оставяше човек с чувството, че паролата е спряла.
    let idleNote = false;
    try { idleNote = sessionStorage.getItem('crm.idleLogout') === '1'; } catch (e) {}
    if (idleNote) card.appendChild(el('div', { class: 'auth-notice', role: 'status', text: 'Излязохте автоматично след ' + IDLE.minutes + ' минути без активност — за сигурност. Влезте отново.' }));

    card.appendChild(errBox);
    if (mode === 'register') card.appendChild(field('Име', name));
    card.appendChild(field('Имейл', email));
    card.appendChild(field('Парола', passWrap));
    card.appendChild(capsNote);
    if (mode === 'register') card.appendChild(field('Код за настройка', setupCode));
    [name, email, pass, setupCode].forEach((i) => i.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); }));
    card.appendChild(el('button', { class: 'btn full', text: mode === 'register' ? 'Създай профил' : 'Влез', onClick: submit, style: 'margin-top:6px' }));
    // (А.6) Къде е изходът при забравена парола — иначе човекът е в задънена
    // улица. От 16.10.2026 — линк по имейл, без да се чака администраторът.
    if (mode === 'login') card.appendChild(el('p', { class: 'auth-forgot' },
      el('button', { class: 'linklike', type: 'button', text: 'Забравена парола?', onClick: () => { mode = 'forgot'; draw(); } })));
  }

  // „Забравена парола" (имейл → линк) и „Нова парола" (от линка в писмото).
  function drawRecovery() {
    const errBox = el('div');
    const okBox = el('div');
    const showErr = (msg) => { clear(errBox); errBox.appendChild(el('div', { class: 'err', role: 'alert', text: msg })); };
    card.appendChild(el('div', { class: 'auth-logo' }, el('img', { class: 'mark', src: '/logo.png', alt: 'BV KONT' }), el('span', { text: 'CRM система' })));
    const back = el('button', { class: 'linklike', type: 'button', text: '← Обратно към входа', onClick: () => {
      mode = 'login';
      try { if (/^#\/reset\//.test(window.location.hash)) history.replaceState(null, '', '#/dashboard'); } catch (e) {}
      draw();
    } });
    if (mode === 'forgot') {
      const email = input({ type: 'email', placeholder: 'name@example.com', autocomplete: 'email' });
      const send = async () => {
        clear(errBox); clear(okBox);
        try {
          await api('/auth/forgot', { method: 'POST', body: JSON.stringify({ email: email.value }) });
          okBox.appendChild(el('div', { class: 'auth-notice', role: 'status', text: 'Ако има профил с този имейл, до него тръгна писмо с линк за нова парола. Линкът важи 1 час — проверете и папка „Спам".' }));
        } catch (e) { showErr(e.message); }
      };
      email.addEventListener('keydown', (e) => { if (e.key === 'Enter') send(); });
      card.appendChild(el('p', { class: 'auth-sub', text: 'Въведете имейла, с който влизате. Ще получите линк за нова парола.' }));
      card.appendChild(errBox); card.appendChild(okBox);
      card.appendChild(field('Имейл', email));
      card.appendChild(el('button', { class: 'btn full', type: 'button', text: 'Изпрати линк', onClick: send, style: 'margin-top:6px' }));
    } else {
      const p1 = input({ type: 'password', autocomplete: 'new-password' });
      const p2 = input({ type: 'password', autocomplete: 'new-password' });
      const save = async () => {
        clear(errBox);
        { const pe = passwordError(p1.value); if (pe) return showErr(pe); }
        if (p1.value !== p2.value) return showErr('Двете пароли не съвпадат.');
        try {
          await api('/auth/reset', { method: 'POST', body: JSON.stringify({ token: resetToken, password: p1.value }) });
          try { history.replaceState(null, '', '#/dashboard'); } catch (e) {}
          mode = 'login';
          draw();
          card.insertBefore(el('div', { class: 'auth-notice', role: 'status', text: 'Новата парола е записана. Влезте с нея.' }), card.children[2] || null);
        } catch (e) { showErr(e.message); }
      };
      [p1, p2].forEach((i) => i.addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); }));
      card.appendChild(el('p', { class: 'auth-sub', text: 'Задайте нова парола — поне ' + PASS_MIN + ' знака. По-лесно се помни фраза от няколко думи.' }));
      card.appendChild(errBox);
      card.appendChild(field('Нова парола', p1));
      card.appendChild(field('Новата парола още веднъж', p2));
      card.appendChild(el('button', { class: 'btn full', type: 'button', text: 'Запиши новата парола', onClick: save, style: 'margin-top:6px' }));
    }
    card.appendChild(el('p', { class: 'auth-forgot' }, back));
  }
  draw();
  root.appendChild(el('div', { class: 'auth-wrap' }, card));
}

// ---- Таймер за бездействие (авто-изход след 20 мин) ----
// Изнесено и променимо, за да могат тестовете да скъсят минутите до
// милисекунди. Продът не пипа тези стойности.
export const IDLE = { minutes: 20, warnBefore: 2 };
let _idleTimer = null;
let _warnTimer = null;
let _warnOverlay = null;
let _lastActivity = Date.now();

export function resetIdleTimer() {
  _lastActivity = Date.now();
  if (!state().user) return;
  clearTimeout(_idleTimer);
  clearTimeout(_warnTimer);
  _idleTimer = setTimeout(idleLogout, IDLE.minutes * 60 * 1000);
  const warnAt = (IDLE.minutes - IDLE.warnBefore) * 60 * 1000;
  if (warnAt > 0) _warnTimer = setTimeout(showIdleWarning, warnAt);
}

// (А.5б) Две минути преди изхода: „Още ли сте тук?". Диалогът се затваря
// само от бутона си или от самия изход — фонът е inert, така че човекът
// така или иначе трябва да мине през него, а кликът е и активност.
function showIdleWarning() {
  if (!state().user) return;
  if (_warnOverlay && _warnOverlay.isConnected) return;
  const stay = () => {
    _warnOverlay = null;
    closeModal();
    resetIdleTimer();
    api('/auth/me').catch(() => {}); // подсеща и сървъра, че сме живи
  };
  _warnOverlay = openModal({
    title: 'Още ли сте тук?',
    body: el('p', { style: 'margin:0', text: 'Няма активност от ' + (IDLE.minutes - IDLE.warnBefore) + ' минути. За сигурност ще излезете автоматично след ' + IDLE.warnBefore + ' минути. Ако имате незаписана форма, тя ще се запази като чернова за преписване.' }),
    foot: el('div', {}, el('button', { class: 'btn', text: 'Остани в системата', onClick: stay })),
    // Не е форма на раздел — „само преглед" не бива да скрива бутона.
    allowEdit: true,
  });
}

// (А.5в) Пипнатата форма се снима като чернова ПРЕДИ да съборим екрана.
function saveIdleDraft() {
  try {
    const d = modalDraft();
    if (d) localStorage.setItem('crm.idleDraft', JSON.stringify(Object.assign({ when: new Date().toISOString() }, d)));
  } catch (e) {}
}

// Показва черновата еднократно след следващия вход (вика се от boot).
export function showIdleDraftIfAny() {
  let d = null;
  try { d = JSON.parse(localStorage.getItem('crm.idleDraft') || 'null'); } catch (e) {}
  try { localStorage.removeItem('crm.idleDraft'); } catch (e) {}
  if (!d || !Array.isArray(d.fields) || !d.fields.length) return;
  const body = el('div', {},
    el('p', { text: 'При автоматичния изход остана незаписана форма „' + d.title + '". Ето въведеното, за да го препишете:' }),
    ...d.fields.map((f) => el('div', { class: 'draft-line' },
      el('b', { text: f.label ? f.label + ': ' : '' }), el('span', { text: f.value }))));
  openModal({ title: 'Незаписана чернова', body, foot: el('div', {}, el('button', { class: 'btn', text: 'Разбрах', onClick: closeModal })), allowEdit: true });
}

async function idleLogout() {
  clearTimeout(_idleTimer);
  clearTimeout(_warnTimer);
  if (!state().user) return;
  saveIdleDraft();
  // Предупреждението е най-отгоре (след него не е имало активност) —
  // затваря се, за да не виси върху екрана за вход.
  if (_warnOverlay && _warnOverlay.isConnected) closeModal();
  _warnOverlay = null;
  try { await api('/auth/logout', { method: 'POST' }); } catch (e) {}
  state().user = null;
  try { sessionStorage.setItem('crm.idleLogout', '1'); } catch (e) {}
  renderAuth();
}

['click', 'keydown', 'mousemove', 'scroll', 'touchstart'].forEach((ev) =>
  document.addEventListener(ev, resetIdleTimer, { passive: true }));
setInterval(() => {
  if (state().user && (Date.now() - _lastActivity) < 5 * 60 * 1000) {
    api('/auth/me').catch(() => {});
  }
}, 5 * 60 * 1000);

// ---- Стартиране ----
export async function boot() {
  const st = state();
  try {
    const me = await api('/auth/me');
    st.user = me.user;
    // Колко трае сесията — както е зададено в сървъра (SESSION_MINUTES), а не
    // твърдите 20 минути (грешка от картата, 16.10.2026).
    if (Number(me.session_minutes) >= 5) IDLE.minutes = Number(me.session_minutes);
    st.companies = await api('/companies');
    if (st.activeCompany && !st.companies.find((c) => c.id === st.activeCompany)) st.activeCompany = null;
    if (!st.activeCompany && st.companies.length) st.activeCompany = st.companies[0].id;
    // Синхронизираме localStorage с реалната активна фирма, за да филтрират правилно
    // и добавките (registry-autofill: търсачката за клиент във фактурата).
    try { if (st.activeCompany) localStorage.setItem('activeCompany', st.activeCompany); else localStorage.removeItem('activeCompany'); } catch (e) {}
    renderShell();
    resetIdleTimer();
    showIdleDraftIfAny();
  } catch (e) {
    renderAuth();
  }
}
